<template>
  <div>
    <v-row
      justify-center
      v-if="view.isLoading"
      v-bind:style="{ 'background-color': dialogLayout ? 'white' : '' }"
      style="background-color: white"
    >
      <v-progress-circular
        :size="55"
        :width="4"
        indeterminate
        color="primary"
        class="mt-4 mb-4 mx-auto"
      ></v-progress-circular>
    </v-row>
    <v-form
      ref="form"
      v-model="view.isValid"
      lazy-validation
      v-if="!view.isLoading"
    >
      <v-alert
        prominent
        width="650"
        color="red"
        type="error"
        border="top"
        v-if="error.active"
      >
        <v-row align="center">
          <v-col class="grow">
            {{ error.text }}
          </v-col>
        </v-row>
      </v-alert>

      <v-row class="mb-2" v-if="!dialogLayout">
        <v-col cols="12">
          <span>
            {{ $t("components.documents.documentAddEditForm.information") }}
          </span>
        </v-col>
      </v-row>

      <v-card
        width="650px"
        v-show="!view.isLoading"
        :class="{ 'mb-12': !dialogLayout }"
        :elevation="!dialogLayout ? 2 : 0"
        :tile="dialogLayout"
        :outlined="!dialogLayout"
      >
        <v-card-title v-if="dialogLayout" :class="{ 'pb-0': dialogLayout }">
          <span class="headline">
            {{ $t("components.documents.documentAddEditForm.addNew") }}
          </span>
        </v-card-title>
        <v-card-text :class="{ 'pb-0': dialogLayout }">
          <v-container>
            <v-row dense>
              <v-col cols="12" sm="12" md="12" lg="12">
                <v-text-field
                  v-model="editedItem.title"
                  prepend-icon="mdi-subtitles-outline"
                  :label="
                    $t('components.documents.documentAddEditForm.documentTitle')
                  "
                  color="primary"
                  required
                  :rules="rules.title"
                  data-cy="documentTitleInput"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="12" md="12" lg="12">
                <document-categories-autocomplete
                  :categories.sync="editedItem.categories"
                />
              </v-col>
              <v-col cols="12" sm="12" md="12" lg="12">
                <group-selector
                  :crm-group-uuid.sync="editedItem.crm_group_uuid"
                  :initial-data="editedItem.crm_group"
                  :select-default="!view.isEdit"
                  :prepand-icon="'mdi-account-group'"
                  :custom-label="
                    $t('components.documents.documentAddEditForm.visibility')
                  "
                  :rules="rules.group"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>

      <v-row class="mb-2" v-if="!dialogLayout">
        <v-col cols="12">
          <span>
            {{ $t("components.documents.documentAddEditForm.resources") }}
          </span>
        </v-col>
      </v-row>

      <v-card
        width="650px"
        v-if="!view.isLoading"
        :class="{ 'mb-12': !dialogLayout }"
        :elevation="!dialogLayout ? 2 : 0"
        :tile="dialogLayout"
        :outlined="!dialogLayout"
      >
        <v-card-text :class="{ 'pt-0': dialogLayout }">
          <v-container>
            <v-row dense>
              <v-col cols="12" sm="12" md="12" lg="12">
                <multilingual-document-selector
                  ref="multilingualDocumentSelector"
                  :attachments.sync="attachments"
                ></multilingual-document-selector>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions v-if="dialogLayout">
          <v-btn
            v-if="view.isEdit && editedItem.is_removable"
            text
            color="red"
            @click="remove"
          >
            <v-icon small class="mr-2"> mdi-delete</v-icon>
            {{ $t("newsfeed.newsFeedAddEdit.delete") }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="close()">
            {{ $t("components.documents.documentAddEditForm.cancel") }}
          </v-btn>
          <v-btn color="primary" @click="save" class="mr-2">
            <span>{{
              $t("components.documents.documentAddEditForm.save")
            }}</span>
          </v-btn>
        </v-card-actions>
      </v-card>

      <v-app-bar
        v-if="!dialogLayout"
        app
        color="white"
        style="z-index: 100; height: 64px; top: unset; bottom: 0"
        bottom
      >
        <v-layout align-center justify-center>
          <v-card flat width="650">
            <v-card-actions>
              <v-btn
                v-if="view.isEdit && editedItem.is_removable"
                text
                color="red"
                @click="remove"
              >
                <v-icon small class="mr-2"> mdi-delete</v-icon>
                {{ $t("newsfeed.newsFeedAddEdit.delete") }}
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn color="primary" text @click="close()">
                {{ $t("components.documents.documentAddEditForm.cancel") }}
              </v-btn>
              <v-btn color="primary" @click="save" class="mr-2">
                <span>{{
                  $t("components.documents.documentAddEditForm.save")
                }}</span>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-layout>
      </v-app-bar>
    </v-form>
    <delete-dialog
      :dialog="deleteDialog"
      :item="documentName"
      @agree="handleRemove"
      v-show="false"
    />
  </div>
</template>

<script>
import api from "@/utils/api.js";
import DocumentCategoriesAutocomplete from "@/components/documents/DocumentCategoriesAutocomplete";
import MultilingualDocumentSelector from "@/components/selectors/MultilingualDocumentSelector";
import DeleteDialog from "@/components/dialogs/DeleteDialog.vue";
import GroupSelector from "@/components/selectors/GroupSelector";
import snackbar from "@/utils/snackbar.js";
import { mapGetters } from "vuex";

export default {
  components: {
    GroupSelector,
    DocumentCategoriesAutocomplete,
    MultilingualDocumentSelector,
    DeleteDialog,
  },
  props: {
    dialogLayout: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    rules: {
      group: [],
      title: [],
    },
    view: {
      isEdit: false,
      isLoading: false,
      isValid: true,
    },
    error: {
      active: false,
      text: "",
    },
    editedItem: {
      uuid: null,
      crm_group: null,
      crm_group_uuid: null,
      title: null,
      categories: [],
    },

    deleteDialog: false,
    documentName: null,

    attachments: [],
  }),

  computed: {
    ...mapGetters(["getDocumentPath"]),
  },
  created() {
    this.setInitialData();

    if (this.$route.name === "documentsEdit") {
      this.getDocument();
      this.$store.commit("DOCUMENTPATH", "documentsEdit");
      this.view.isEdit = true;
    } else {
      this.$store.commit("DOCUMENTPATH", this.$route.name);
      this.view.isEdit = false;
    }
  },

  // updated() {
  //   this.$nextTick(function () {
  //     this.$watch("editedItem", this.formEdited, {
  //       deep: true,
  //     });
  //   });
  // },

  methods: {
    setInitialData() {
      this.documentTypeOptions = [
        {
          name: this.$t("components.documents.documentAddEditForm.file"),
          value: true,
        },
        {
          name: this.$t("components.documents.documentAddEditForm.link"),
          value: false,
        },
      ];
      this.rules.group = [
        (v) =>
          !!v ||
          this.$t("components.documents.documentAddEditForm.groupIsRequired"),
      ];
      this.rules.title = [
        (v) =>
          !!v ||
          this.$t(
            "components.documents.documentAddEditForm.documentTitleIsRequired"
          ),
      ];

      this.documentTypeOptions = [
        {
          name: this.$t("components.documents.documentAddEditForm.file"),
          value: true,
        },
        {
          name: this.$t("components.documents.documentAddEditForm.link"),
          value: false,
        },
      ];
    },
    formEdited: function (newV, oldV) {
      this.$emit("documentEdited", true);
    },
    close(uuid) {
      if (
        (this.getDocumentPath === "documentsEdit" ||
          this.getDocumentPath === "documentsAdd") &&
        typeof uuid !== "undefined"
      ) {
        this.$router.push({
          name: "documentsDetails",
          params: { id: uuid },
        });
      } else if (
        this.getDocumentPath === "documentsEdit" ||
        this.getDocumentPath === "documentsAdd"
      ) {
        this.$router.push({
          name: "documents",
        });
      } else {
        this.$store.commit("DOCUMENTDIALOGSTATUS", false);
      }
    },

    handleError(err) {
      this.$refs.multilingualDocumentSelector.checkNumberOfAttachments();
      this.error.active = true;
      this.error.text = this.$t(
        "components.documents.documentAddEditForm.pleaseFillAllTheRequiredFields"
      );
    },

    applyTemplate(value) {
      this.getDocument(value.uuid);
    },

    remove() {
      this.deleteDialog = true;
      this.documentName = this.editedItem.title;
    },

    handleRemove(agreed) {
      if (agreed) {
        api.deleteDocument(this.editedItem.uuid).then((resp) => {
          this.$router.push({
            name: "documents",
          });
        });
      }
      this.deleteDialog = false;
    },

    getDocument(uuid = null) {
      this.view.isLoading = true;
      uuid = uuid === null ? this.$route.params.id : uuid;

      api.getDocument(uuid).then((resp) => {
        this.editedItem = resp.data;
        this.attachments = this.editedItem.file_resources.map((item) => {
          return {
            ...item,
            name: item.link,
            internalType: item.type !== "link" ? "file" : "link",
            dirty: false,
          };
        });
        this.editedItem.crm_group_uuid = this.editedItem.crm_group.uuid;
        this.view.isLoading = false;
      });
    },

    save() {
      if (this.$refs.form.validate()) {
        this.$refs.multilingualDocumentSelector.addAttachmentIfExists();

        if (this.attachments.length === 0) {
          this.handleError();
        }

        this.view.isLoading = true;
        if (!this.view.isEdit) {
          this.$refs.multilingualDocumentSelector
            .save(
              this.editedItem.categories,
              this.editedItem.title,
              this.editedItem.crm_group_uuid
            )
            .then((value) => {
              const payload = {
                title: this.editedItem.title,
                categories: this.editedItem.categories,
                crm_group_uuid: this.editedItem.crm_group_uuid,
                file_resource_uuids: [
                  ...value.files,
                  ...value.alreadyAddedFiles,
                ],
                links: value.links,
                visiblilty_document_uuid: this.editedItem.crm_group_uuid,
              };
              api
                .createDocument(payload)
                .then((resp) => {
                  this.$store.commit("DOCUMENTADDED", { document: resp.data });
                  this.close(resp.data.uuid);
                })
                .catch((err) => {
                  this.close();
                });
            })
            .catch((err) => {
              this.close();
            });
        } else {
          let categories = [];
          if (
            this.editedItem.file_resource &&
            this.editedItem.file_resource.categories
          ) {
            categories = this.editedItem.file_resource.categories;
          }

          this.$refs.multilingualDocumentSelector
            .save(categories, this.editedItem.crm_group_uuid)
            .then((value) => {
              const payload = {
                title: this.editedItem.title,
                categories: this.editedItem.categories,
                crm_group_uuid: this.editedItem.crm_group_uuid,
                file_resource_uuids: [
                  ...value.files,
                  ...value.alreadyAddedFiles,
                ],
                links: value.links,
                visiblilty_document_uuid: this.editedItem.crm_group_uuid,
              };
              api
                .updateDocument(this.editedItem.uuid, payload)
                .then((resp) => {
                  this.$store.commit("DOCUMENTADDED", { document: resp.data });
                  this.close(resp.data.uuid);
                })
                .catch((err) => {
                  this.error.active = true;
                  this.error.text = "An error has occured";
                  this.close();
                });
            })
            .catch((err) => {
              this.close();
            });
        }
      } else {
        this.handleError();
      }
    },
  },
};
</script>

<style></style>
